import React, { useState, useEffect, useRef } from 'react';
import './Blog.css';
import api from '../../api/axios';
import { API_IMAGE_BASE_URL } from '../../config/api';

const EMPTY_FORM = {
  title: '', excerpt: '', content: '', category: 'General',
  tags: '', author: 'Admin', isPublished: false,
  metaTitle: '', metaDescription: '',
};

const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [coverImage, setCoverImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);
  const modalRef = useRef(null);

  const fetchBlogs = async () => {
    try {
      const res = await api.get(`/admin/blogs?search=${search}&page=${page}&limit=10&status=${statusFilter}`);
      setBlogs(res.data?.data || []);
      setPagination(res.data?.pagination || { total: 0, page: 1, totalPages: 1 });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchBlogs(); }, [search, page, statusFilter]);

  useEffect(() => {
    const handleOutside = (e) => {
      if (showModal && modalRef.current && !modalRef.current.contains(e.target)) resetForm();
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [showModal]);

  const resetForm = () => {
    setShowModal(false);
    setEditingBlog(null);
    setForm(EMPTY_FORM);
    setCoverImage(null);
    setImagePreview(null);
  };

  const handleEdit = (blog) => {
    setEditingBlog(blog);
    setForm({
      title: blog.title || '',
      excerpt: blog.excerpt || '',
      content: blog.content || '',
      category: blog.category || 'General',
      tags: Array.isArray(blog.tags) ? blog.tags.join(', ') : '',
      author: blog.author || 'Admin',
      isPublished: blog.isPublished || false,
      metaTitle: blog.metaTitle || '',
      metaDescription: blog.metaDescription || '',
    });
    setImagePreview(blog.coverImage ? `${API_IMAGE_BASE_URL}${blog.coverImage}` : null);
    setCoverImage(null);
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      alert('Title and content are required');
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (coverImage) fd.append('coverImage', coverImage);
      if (editingBlog) {
        await api.put(`/admin/blogs/${editingBlog._id}`, fd);
      } else {
        await api.post('/admin/blogs', fd);
      }
      resetForm();
      fetchBlogs();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to save blog');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/blogs/${deleteTarget._id}`);
      fetchBlogs();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteTarget(null);
      setShowDeleteModal(false);
    }
  };

  return (
    <div className="blog-management">
      <div className="page-header">
        <h1>Blog Management</h1>
        <p>Create and manage blog posts for your website</p>
      </div>

      <div className="blog-controls">
        <input
          type="text"
          placeholder="Search blogs..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="search-input"
        />
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="status-filter">
          <option value="">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </select>
        <button className="add-btn" onClick={() => setShowModal(true)}>+ Add Blog</button>
      </div>

      {/* Desktop table */}
      <div className="blog-table-wrap">
        <table className="blog-table">
          <thead>
            <tr>
              <th>Cover</th>
              <th>Title</th>
              <th>Category</th>
              <th>Author</th>
              <th>Status</th>
              <th>Views</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {blogs.map((blog) => (
              <tr key={blog._id}>
                <td>
                  {blog.coverImage ? (
                    <img src={`${API_IMAGE_BASE_URL}${blog.coverImage}`} alt={blog.title} className="blog-thumb" />
                  ) : (
                    <div className="blog-thumb-placeholder">No Img</div>
                  )}
                </td>
                <td>
                  <div className="blog-title-cell">{blog.title}</div>
                  <div className="blog-slug-cell">{blog.slug}</div>
                </td>
                <td><span className="category-badge">{blog.category}</span></td>
                <td>{blog.author}</td>
                <td>
                  <span className={`status-badge ${blog.isPublished ? 'published' : 'draft'}`}>
                    {blog.isPublished ? 'Published' : 'Draft'}
                  </span>
                </td>
                <td>{blog.views || 0}</td>
                <td>{new Date(blog.createdAt).toLocaleDateString()}</td>
                <td className="actions-cell">
                  <button className="btn-edit" onClick={() => handleEdit(blog)}>Edit</button>
                  <button className="btn-delete" onClick={() => { setDeleteTarget(blog); setShowDeleteModal(true); }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {blogs.length === 0 && <div className="no-data">No blogs found.</div>}
      </div>

      {/* Mobile card list */}
      <div className="blog-card-list">
        {blogs.map((blog) => (
          <div key={blog._id} className="blog-mobile-card">
            <div className="bmc-top">
              {blog.coverImage ? (
                <img src={`${API_IMAGE_BASE_URL}${blog.coverImage}`} alt={blog.title} className="bmc-img" />
              ) : (
                <div className="bmc-img-placeholder">No Img</div>
              )}
              <div className="bmc-info">
                <div className="bmc-title">{blog.title}</div>
                <div className="bmc-slug">{blog.slug}</div>
                <div className="bmc-meta">
                  <span className="category-badge">{blog.category}</span>
                  <span className={`status-badge ${blog.isPublished ? 'published' : 'draft'}`}>
                    {blog.isPublished ? 'Published' : 'Draft'}
                  </span>
                </div>
                <div className="bmc-sub">By {blog.author} · {blog.views || 0} views · {new Date(blog.createdAt).toLocaleDateString()}</div>
              </div>
            </div>
            <div className="bmc-actions">
              <button className="btn-edit" onClick={() => handleEdit(blog)}>Edit</button>
              <button className="btn-delete" onClick={() => { setDeleteTarget(blog); setShowDeleteModal(true); }}>Delete</button>
            </div>
          </div>
        ))}
        {blogs.length === 0 && <div className="no-data">No blogs found.</div>}
      </div>

      <div className="pagination">
        <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
        <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)</span>
        <button disabled={page >= pagination.totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="blog-modal" ref={modalRef}>
            <div className="modal-header">
              <h2>{editingBlog ? 'Edit Blog' : 'Add Blog'}</h2>
              <button className="close-btn" onClick={resetForm}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Blog title" />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Wedding Tips" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Author</label>
                  <input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Tags (comma separated)</label>
                  <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="wedding, tips, vendors" />
                </div>
              </div>
              <div className="form-group">
                <label>Excerpt</label>
                <textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Short description shown in listing" />
              </div>
              <div className="form-group">
                <label>Content *</label>
                <textarea rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Full blog content (HTML supported)" />
              </div>
              <div className="form-group">
                <label>Cover Image</label>
                <input type="file" accept="image/*" ref={fileRef} onChange={handleImageChange} style={{ display: 'none' }} />
                <button type="button" className="upload-btn" onClick={() => fileRef.current.click()}>
                  {imagePreview ? 'Change Image' : 'Upload Image'}
                </button>
                {imagePreview && <img src={imagePreview} alt="preview" className="image-preview" />}
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Meta Title</label>
                  <input value={form.metaTitle} onChange={(e) => setForm({ ...form, metaTitle: e.target.value })} placeholder="SEO title" />
                </div>
                <div className="form-group">
                  <label>Meta Description</label>
                  <input value={form.metaDescription} onChange={(e) => setForm({ ...form, metaDescription: e.target.value })} placeholder="SEO description" />
                </div>
              </div>
              <div className="form-group publish-toggle">
                <label>
                  <input type="checkbox" checked={form.isPublished} onChange={(e) => setForm({ ...form, isPublished: e.target.checked })} />
                  &nbsp; Publish immediately
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={resetForm}>Cancel</button>
              <button className="save-btn" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Saving...' : editingBlog ? 'Update Blog' : 'Create Blog'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="delete-modal">
            <h3>Delete Blog</h3>
            <p>Are you sure you want to delete "<strong>{deleteTarget?.title}</strong>"?</p>
            <div className="delete-actions">
              <button className="cancel-btn" onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}>Cancel</button>
              <button className="confirm-delete-btn" onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogManagement;
